import { useEffect } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { Answer, Question } from '../types/form';
import { BrandMark } from './BrandMark';
import { ImageOptionCard } from './ImageOptionCard';
import { ListOptionRow } from './ListOptionRow';
import { ProgressRail } from './ProgressRail';
interface QuestionScreenProps {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  answer: Answer;
  showProgress: boolean;
  isLast: boolean;
  onToggleOption: (optionId: string) => void;
  onOtherChange: (value: string) => void;
  onBack: () => void;
  onNext: () => void;
}
export function QuestionScreen({
  question,
  questionNumber,
  totalQuestions,
  answer,
  showProgress,
  isLast,
  onToggleOption,
  onOtherChange,
  onBack,
  onNext
}: QuestionScreenProps) {
  const otherOption = question.options.find(o => o.isOther);
  const otherSelected = !!otherOption && answer.optionIds.includes(otherOption.id);
  const canContinue = answer.optionIds.length > 0 && (!otherSelected || answer.otherText.trim().length > 0);
  useEffect(() => {
    function handleKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
      if (event.key === 'Enter' && canContinue) {
        event.preventDefault();
        onNext();
        return;
      }
      if (typing) return;
      const numeric = Number(event.key);
      if (numeric >= 1 && numeric <= question.options.length) {
        event.preventDefault();
        onToggleOption(question.options[numeric - 1].id);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [canContinue, onNext, onToggleOption, question]);
  const rankedOptions = question.options.filter(o => !o.isOther);
  return <section className="min-h-screen w-full bg-olive-800 px-6 py-10 sm:px-10 lg:px-16 lg:py-14" data-id="element-54" data-mp="13rpoksxoympm">
      <header className="flex items-center justify-between gap-6" data-id="element-55" data-mp="1dnpsp4d57irl">
        <BrandMark data-id="element-56" data-mp="1dkttzdth0q23" />
        {showProgress && <ProgressRail total={totalQuestions} current={questionNumber} data-id="element-57" data-mp="18ntq99k3fze5" />}
      </header>

      <div className="mt-12 grid grid-cols-1 gap-10 lg:mt-16 lg:grid-cols-[minmax(300px,0.75fr)_1.35fr] lg:gap-16" data-id="element-58" data-mp="13oh1ukdkqk9g">
        <div className="lg:sticky lg:top-14 lg:self-start" data-id="element-59" data-mp="yovrldsyephf">
          <p className="font-sans text-[11px] uppercase tracking-widest2 text-gold-400" data-id="element-60" data-mp="uobto80k3hu8">
            {question.eyebrow}
          </p>
          <h1 className="mt-5 font-display text-[40px] leading-[1.08] text-cream-50 lg:text-5xl" data-id="element-61" data-mp="pxc1th6anpyl">
            {question.title}
          </h1>
          {question.helper && <p className="mt-5 max-w-sm font-sans text-sm leading-relaxed text-cream-100/65" data-id="element-62" data-mp="fzo4cjhb04cq">
              {question.helper}
            </p>}

          <div className="mt-10 flex items-center gap-4" data-id="element-63" data-mp="azjkqkl3mtia">
            <button type="button" onClick={onBack} className="inline-flex h-12 w-12 items-center justify-center rounded-sm border border-cream-100/25 text-cream-100/70 transition-colors duration-200 ease-soft hover:border-cream-100/60 hover:text-cream-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400" aria-label="Voltar" data-id="element-64" data-mp="6yhj5x3pm5d5">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-65" data-mp="2wkt4wcvggsef" />
            </button>

            <button type="button" onClick={onNext} disabled={!canContinue} className="group inline-flex items-center gap-3 rounded-sm bg-cream-100 px-7 py-4 font-sans text-sm uppercase tracking-[0.2em] text-olive-800 transition-colors duration-200 ease-soft hover:bg-cream-50 disabled:cursor-not-allowed disabled:bg-cream-100/20 disabled:text-cream-100/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 focus-visible:ring-offset-olive-800" data-id="element-66" data-mp="1yyvii4dyg2x7">
              {isLast ? 'Enviar' : 'Continuar'}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-enabled:group-hover:translate-x-1" aria-hidden="true" data-id="element-67" data-mp="3mwwhy3w4wwse" />
            </button>
          </div>

          <p className="mt-5 font-sans text-[11px] uppercase tracking-[0.18em] text-cream-100/40" data-id="element-68" data-mp="id6aeyjnvpm">
            {question.multi ? 'Selecione uma ou mais opções' : 'Selecione uma opção'}
          </p>
        </div>

        <div role={question.multi ? 'group' : 'radiogroup'} aria-label={question.title} data-id="element-69" data-mp="agapgo1jr2bd">
          {question.layout === 'image' ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-6" data-id="element-70" data-mp="jfee07jhg9x9">
              {question.options.map((option, index) => {
            const featured = index < 2;
            return <div key={option.id} className={`h-full ${featured ? 'sm:col-span-3' : 'sm:col-span-2'}`} data-id="element-71" data-mp="2l5pt1lzrzqrj" data-mpi={index}>
                    <ImageOptionCard option={option} shortcut={String(index + 1)} featured={featured} multi={!!question.multi} selected={answer.optionIds.includes(option.id)} otherText={answer.otherText} onSelect={() => onToggleOption(option.id)} onOtherChange={onOtherChange} data-id="element-72" data-mp="vpdp9417htc9" />
                  </div>;
          })}
            </div> : <div className="border-t border-cream-100/12" data-id="element-73" data-mp="hzaaey4np1wu">
              {question.options.map((option, index) => <ListOptionRow key={option.id} option={option} shortcut={String(index + 1)} layout={question.layout === 'scale' ? 'scale' : 'timeline'} rank={rankedOptions.findIndex(o => o.id === option.id)} steps={rankedOptions.length} selected={answer.optionIds.includes(option.id)} otherText={answer.otherText} onSelect={() => onToggleOption(option.id)} onOtherChange={onOtherChange} data-id="element-74" data-mp="40kdbihu728h" data-mpi={index} />)}
            </div>}
        </div>
      </div>
    </section>;
}